import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { FarmBlueprintMap } from '@/components/map/FarmBlueprintMap';
import { PondQuickViewModal } from '@/components/pond/PondQuickViewModal';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Card } from '@/components/ui/Card';
import { NoFarmsEmptyState } from '@/components/farm';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { useCurrentFarm } from '@/hooks/useCurrentFarm';
import { useMembership } from '@/hooks/useMembership';
import type { FarmOverviewResponse, PondOverview, ApiResponse } from '@/types/api';

export const Route = createFileRoute('/_app/map')({
  component: FarmMapPage,
});

function FarmMapPage() {
  const { farmId, farms, isLoading: isFarmLoading } = useCurrentFarm();
  const { isOperator } = useMembership();
  const [selectedPond, setSelectedPond] = useState<PondOverview | null>(null);

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery<ApiResponse<FarmOverviewResponse>>({
    queryKey: ['farm', 'overview', farmId],
    queryFn: () => api.get(`/api/v1/farms/${farmId}/overview`),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    enabled: !!farmId,
  });

  const farm = data?.data;

  // No farms at all: clean empty state instead of the load-error card.
  if (!isFarmLoading && farms.length === 0) {
    return <NoFarmsEmptyState isOperator={isOperator} />;
  }

  if (isFarmLoading || isLoading || (!farmId && !error)) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <LoadingSpinner size={32} className="text-primary-600" />
        <p className="text-gray-500">Loading farm data...</p>
      </div>
    );
  }

  if (error || !farm) {
    return (
      <Card className="border-status-critical/30">
        <Card.Body>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <AlertCircle className="h-6 w-6 text-status-critical" />
            </div>
            <h3 className="font-semibold text-gray-900">Failed to load farm data</h3>
            <p className="text-sm text-gray-500 mt-1 mb-4">
              {error instanceof Error ? error.message : 'Please try again'}
            </p>
            <button
              onClick={() => refetch()}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              Retry
            </button>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Farm Map</h1>
        <p className="text-gray-500">{farm.name} — Satellite View</p>
      </div>

      {/* Blueprint Map */}
      <FarmBlueprintMap
        ponds={farm.ponds}
        onPondClick={setSelectedPond}
        farmId={farm.id}
      />

      {/* Quick-View Modal */}
      <PondQuickViewModal
        pond={selectedPond}
        open={selectedPond !== null}
        onClose={() => setSelectedPond(null)}
        alertThresholds={farm.alert_thresholds}
      />
    </div>
  );
}
